import React, { useEffect, useMemo, useState } from "react";

// ------------------------------------------------------------
// Abschluss-Checker – Heisterbergschule (OBS Ahlem)
// ------------------------------------------------------------
// Hinweis: Die Heisterbergschule (Oberschule Ahlem, Hannover)
// führt keinen Gymnasialzweig. Abschlüsse nach Klasse 10:
// - Hauptschulabschluss
// - Sekundarabschluss I – Realschulabschluss
// - Erweiterter Sekundarabschluss I
// ------------------------------------------------------------

type CourseLevel = "none" | "G" | "E";
type CoreKind = "none" | "Deutsch" | "Mathematik" | "Englisch";

interface Subject {
  id: string;
  name: string;
  grade: number | null; // null = nicht erteilt (n.e.)
  level: CourseLevel;
  core: CoreKind;
}

const fixedSubjects: Omit<Subject, "id">[] = [
  { name: "Deutsch", grade: 3, level: "G", core: "Deutsch" },
  { name: "Mathematik", grade: 3, level: "G", core: "Mathematik" },
  { name: "Englisch", grade: 3, level: "G", core: "Englisch" },
  { name: "Informatik", grade: 3, level: "none", core: "none" },
  { name: "Erdkunde", grade: 3, level: "none", core: "none" },
  { name: "Politik", grade: 3, level: "none", core: "none" },
  { name: "Sport", grade: null, level: "none", core: "none" },
  { name: "Chemie", grade: 3, level: "G", core: "none" },
  { name: "Physik", grade: 3, level: "G", core: "none" },
  { name: "Kunst", grade: 3, level: "none", core: "none" },
  { name: "Biologie", grade: 3, level: "none", core: "none" },
  { name: "Arbeit-Wirtschaft", grade: 3, level: "none", core: "none" },
];

const profiles = ["Französisch", "Gesundheit und Soziales", "Technik", "Wirtschaft"] as const;

function uid() { return Math.random().toString(36).slice(2); }
function clamp(n: number, min: number, max: number) { return Math.max(min, Math.min(max, n)); }

// Ø-Berechnung mit E-Kurs-Bonus (§22 Abs. 3); n.e. (null) wird ausgeschlossen
function averageWithEKursBonus(subjects: Subject[]) {
  const valid = subjects.filter(s => s.grade !== null);
  if (valid.length === 0) return Number.NaN;
  const grades = valid.map(s => (s.level === "E" ? Math.max(1, (s.grade as number) - 1) : (s.grade as number)));
  return Math.round((grades.reduce((a, b) => a + b, 0) / grades.length) * 100) / 100;
}

// Mindestanforderungen gem. §§23–24
function meetsMinimum(subjects: Subject[]) {
  const deficits = subjects.filter(s => s.grade !== null && (s.grade as number) > 4);
  if (deficits.length === 0) return true;
  if (deficits.length === 1 && deficits[0].grade === 5) return true;
  return false;
}

// Abschlussberechnung
function calcOBS(subjects: Subject[]) {
  const avgAll = averageWithEKursBonus(subjects);
  const core = subjects.filter(s => s.grade !== null && ["Deutsch", "Mathematik", "Englisch"].includes(s.core));
  const avgCore = averageWithEKursBonus(core);
  const minOK = meetsMinimum(subjects);
  const gradedCount = subjects.filter(s => s.grade !== null).length;
  const coreCount = core.length;

  const Erw = minOK && Number.isFinite(avgAll) && Number.isFinite(avgCore) && coreCount >= 3 && avgAll <= 3.0 && avgCore <= 3.0;
  const RS = minOK && gradedCount > 0;
  const HS = RS;

  return { Erw, RS, HS, notes: { avgAll, avgCore } };
}

export default function App() {
  const [subs, setSubs] = useState<Subject[]>(fixedSubjects.map(s => ({ ...s, id: uid() })));
  const [profile, setProfile] = useState<typeof profiles[number]>(profiles[0]);
  const [profileGrade, setProfileGrade] = useState<number>(3);

  const subjects = useMemo(
    () => [
      ...subs,
      { id: "__profil__", name: `Profil: ${profile}`, grade: profileGrade, level: "none" as CourseLevel, core: "none" as CoreKind },
    ],
    [subs, profile, profileGrade]
  );

  const result = useMemo(() => calcOBS(subjects), [subjects]);

  let achieved = "(noch kein Abschluss erreicht)";
  let explanation = "";
  if (result.Erw) {
    achieved = "Erweiterter Sekundarabschluss I";
    explanation = "Ø gesamt und Ø D/E/M jeweils ≤ 3,0 (mit E-Kurs-Bonus).";
  } else if (result.RS) {
    achieved = "Sekundarabschluss I – Realschulabschluss";
    explanation = "Mindestanforderungen erfüllt (§§ 23–24).";
  } else if (result.HS) {
    achieved = "Hauptschulabschluss nach Klasse 10";
    explanation = "Mindestanforderungen erreicht (§ 23).";
  }

  function setGrade(id: string, v: string) {
    if (v === "ne") {
      setSubs(s => s.map(x => (x.id === id ? { ...x, grade: null } : x)));
    } else {
      const num = parseInt(v);
      if (!isNaN(num)) setSubs(s => s.map(x => (x.id === id ? { ...x, grade: clamp(num, 1, 6) } : x)));
    }
  }

  function setLevel(id: string, v: CourseLevel) {
    setSubs(s => s.map(x => (x.id === id ? { ...x, level: v } : x)));
  }

  return (
    <div className="p-4">
      <header className="mb-6 flex items-center gap-4">
        <img src="https://www.heisterbergschule.de/assets/images/logo.png" alt="Logo" className="h-16 w-auto"/>
        <div>
          <h1 className="text-2xl font-bold">Heisterbergschule – OBS Ahlem</h1>
          <p className="text-sm text-slate-600">Abschluss-Checker</p>
        </div>
      </header>

      <section className="mb-4">
        <h2 className="font-semibold">Ergebnis</h2>
        <p>{achieved}</p>
        <p className="text-sm text-slate-600">{explanation}</p>
        <ul>
          <li>Ø gesamt: {Number.isFinite(result.notes.avgAll) ? result.notes.avgAll.toFixed(2) : "—"}</li>
          <li>Ø D/E/M: {Number.isFinite(result.notes.avgCore) ? result.notes.avgCore.toFixed(2) : "—"}</li>
        </ul>
      </section>

      <section className="mb-4">
        <h2 className="font-semibold">Profil</h2>
        <select value={profile} onChange={e=>setProfile(e.target.value as any)}>
          {profiles.map(p => <option key={p}>{p}</option>)}
        </select>
        <input type="number" min={1} max={6} value={profileGrade} onChange={e=>setProfileGrade(clamp(parseInt(e.target.value||"0"),1,6))}/>
      </section>

      <section>
        <h2 className="font-semibold">Fächer</h2>
        <table>
          <thead>
            <tr><th>Fach</th><th>Note</th><th>Kurs</th></tr>
          </thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>
                  <select value={s.grade === null ? "ne" : s.grade.toString()} onChange={e=>setGrade(s.id, e.target.value)}>
                    <option value="ne">n.e.</option>
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </td>
                <td>
                  {(["Deutsch","Mathematik","Englisch"].includes(s.core) || ["Chemie","Physik"].includes(s.name)) ? (
                    <select value={s.level} onChange={e=>setLevel(s.id, e.target.value as CourseLevel)}>
                      <option value="G">G-Kurs</option>
                      <option value="E">E-Kurs</option>
                    </select>
                  ) : "–"}
                </td>
              </tr>
            ))}
            <tr>
              <td>Profil: {profile}</td>
              <td>{profileGrade}</td>
              <td>–</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
